import React, { useMemo } from 'react';

import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
	INPUT_METHOD,
} from '@atlaskit/editor-common/analytics/types/enums';
import { IconImages } from '@atlaskit/editor-common/assets';
import { toolbarInsertBlockMessages as messages } from '@atlaskit/editor-common/messages/insert-block';
import type { MediaProvider } from '@atlaskit/editor-common/provider-factory/media-provider';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { areToolbarFlagsEnabled } from '@atlaskit/editor-common/toolbar-flag-check';
import type { EditorAppearance } from '@atlaskit/editor-common/types/editor-appearance';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { PMPlugin, PMPluginFactoryParams } from '@atlaskit/editor-common/types/plugin-factory';
import {
	type NamedPluginStatesFromInjectionAPI,
	useSharedPluginStateWithSelector,
} from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { NodeSelection, PluginKey } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { getMediaFeatureFlag } from '@atlaskit/media-common';
import type { MediaViewerExtensions } from '@atlaskit/media-viewer/types';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import type { MediaNextEditorPluginType } from './mediaPluginType';
import { lazyMediaGroupView } from './nodeviews/lazy-media-group';
import { lazyMediaInlineView } from './nodeviews/lazy-media-inline';
import { ReactMediaNode } from './nodeviews/mediaNodeView';
import { ReactMediaSingleNode } from './nodeviews/mediaSingle';
import { mediaSpecWithFixedToDOM } from './nodeviews/toDOM-fixes/media';
import { mediaGroupSpecWithFixedToDOM } from './nodeviews/toDOM-fixes/mediaGroup';
import { mediaInlineSpecWithFixedToDOM } from './nodeviews/toDOM-fixes/mediaInline';
import { mediaSingleSpecWithFixedToDOM } from './nodeviews/toDOM-fixes/mediaSingle';
import { createAIGeneratingDecorationPlugin } from './pm-plugins/ai-generating-decoration';
import { createPlugin as createMediaAltTextPlugin } from './pm-plugins/alt-text';
import keymapMediaAltTextPlugin from './pm-plugins/alt-text/keymap';
import {
	clearAIGenerating,
	hideMediaViewer,
	insertMediaAsMediaSingleCommand,
	setAIGenerating,
	showMediaViewer,
	trackMediaPaste,
} from './pm-plugins/commands';
import keymapPlugin from './pm-plugins/keymap';
import keymapMediaSinglePlugin from './pm-plugins/keymap-media';
import linkingPlugin from './pm-plugins/linking';
import keymapLinkingPlugin from './pm-plugins/linking/keymap';
import { createPlugin } from './pm-plugins/main';
import { createPlugin as createMediaPixelResizingPlugin } from './pm-plugins/pixel-resizing';
import { stateKey } from './pm-plugins/plugin-key';
import { createMediaIdentifierArray, extractMediaNodes } from './pm-plugins/utils/media-common';
import { insertMediaAsMediaSingle } from './pm-plugins/utils/media-single';
import { MediaPickerComponents } from './ui/MediaPicker';
import { RenderMediaViewer } from './ui/MediaViewer/PortalWrapper';
import { floatingToolbar } from './ui/toolbar';
import ToolbarMedia from './ui/ToolbarMedia';

type MediaPickerFunctionalComponentProps = {
	api: ExtractInjectionAPI<MediaNextEditorPluginType> | undefined;
	appearance: EditorAppearance;
	editorDomElement: Element;
};

type MediaViewerFunctionalComponentProps = {
	api: ExtractInjectionAPI<MediaNextEditorPluginType> | undefined;
	editorView: EditorView;
	fallbackMediaNameFetcher?: (id: string) => Promise<string>;
	mediaViewerExtensions?: MediaViewerExtensions;
};

const selector = (
	states: NamedPluginStatesFromInjectionAPI<
		ExtractInjectionAPI<MediaNextEditorPluginType>,
		'media'
	>,
) => {
	return {
		onPopupToggle: states.mediaState?.onPopupToggle,
		setBrowseFn: states.mediaState?.setBrowseFn,
	};
};

const MediaPickerFunctionalComponent = ({
	api,
	editorDomElement,
	appearance,
}: MediaPickerFunctionalComponentProps) => {
	const { onPopupToggle, setBrowseFn } = useSharedPluginStateWithSelector(api, ['media'], selector);
	if (!onPopupToggle || !setBrowseFn) {
		return null;
	}

	return (
		<MediaPickerComponents
			onPopupToggle={onPopupToggle}
			setBrowseFn={setBrowseFn}
			editorDomElement={editorDomElement}
			appearance={appearance}
			api={api}
		/>
	);
};

const mediaViewerStateSelector = (
	states: NamedPluginStatesFromInjectionAPI<
		ExtractInjectionAPI<MediaNextEditorPluginType>,
		'media'
	>,
) => {
	return {
		isMediaViewerVisible: states.mediaState?.isMediaViewerVisible,
		mediaViewerSelectedMedia: states.mediaState?.mediaViewerSelectedMedia,
		mediaClientConfig: states.mediaState?.mediaClientConfig,
	};
};

const MediaViewerFunctionalComponent = ({
	api,
	editorView,
	mediaViewerExtensions,
	fallbackMediaNameFetcher,
}: MediaViewerFunctionalComponentProps) => {
	// Only traverse document once when media viewer is visible, media viewer items will not update
	// when document changes are made while media viewer is open
	const { isMediaViewerVisible, mediaViewerSelectedMedia, mediaClientConfig } =
		useSharedPluginStateWithSelector(api, ['media'], mediaViewerStateSelector);

	const mediaItems = useMemo(() => {
		if (isMediaViewerVisible) {
			const mediaNodes = extractMediaNodes(editorView.state.doc);
			return createMediaIdentifierArray(mediaNodes);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- only update mediaItems once when media viewer is visible
	}, [isMediaViewerVisible]);

	// Viewer does not have required attributes to render the media viewer
	if (!isMediaViewerVisible || !mediaViewerSelectedMedia || !mediaClientConfig) {
		return null;
	}

	const handleOnClose = () => {
		// Run Command to hide the media viewer
		api?.core.actions.execute(api?.media.commands.hideMediaViewer);
	};

	return (
		<RenderMediaViewer
			mediaClientConfig={mediaClientConfig}
			onClose={handleOnClose}
			selectedNodeAttrs={mediaViewerSelectedMedia}
			items={mediaItems}
			extensions={mediaViewerExtensions}
			fallbackMediaNameFetcher={fallbackMediaNameFetcher}
		/>
	);
};

export const mediaPlugin: MediaNextEditorPluginType = ({ config: options = {}, api }) => {
	let previousMediaProvider: Promise<MediaProvider> | undefined;
	const mediaErrorLocalIds = new Set<string>();

	return {
		name: 'media',

		getSharedState(editorState) {
			if (!editorState) {
				return null;
			}
			return stateKey.getState(editorState) || null;
		},

		actions: {
			handleMediaNodeRenderError: (node: PMNode, reason: string, nestedUnder?: string) => {
				let isDuplicateError = false;

				if (isExperimentEnabled('platform_editor_media_reliability_observability')) {
					if (mediaErrorLocalIds.has(node.attrs.localId)) {
						// we mark duplicate errors in the case of nested media nodes
						// renderering to avoid firing multiple errored events for the same underlying issue,
						// which can happen more often with nested media nodes
						isDuplicateError = true;
					} else {
						mediaErrorLocalIds.add(node.attrs.localId);
					}
				}

				api?.analytics?.actions.fireAnalyticsEvent({
					action: ACTION.ERRORED,
					actionSubject: ACTION_SUBJECT.EDITOR,
					actionSubjectId: ACTION_SUBJECT_ID.MEDIA,
					eventType: EVENT_TYPE.UI,
					attributes: {
						reason,
						external: node.attrs.__external,
						...(nestedUnder ? { nestedUnder } : {}),
						isDuplicateError,
					},
				});
			},

			insertMediaAsMediaSingle: (view, node, inputMethod, insertMediaVia) =>
				insertMediaAsMediaSingle(
					view,
					node,
					inputMethod,
					api?.analytics?.actions,
					insertMediaVia,
					options?.allowPixelResizing,
				),
			setProvider: (provider) => {
				// Prevent someone trying to set the exact same provider twice for performance reasons
				if (previousMediaProvider === provider) {
					return false;
				}
				previousMediaProvider = provider;
				return (
					api?.core.actions.execute(({ tr }) =>
						tr.setMeta(stateKey, { mediaProvider: provider }),
					) ?? false
				);
			},
		},
		commands: {
			showMediaViewer,
			hideMediaViewer,
			trackMediaPaste,
			setAIGenerating,
			clearAIGenerating,
			insertMediaSingle: insertMediaAsMediaSingleCommand(
				api?.analytics?.actions,
				options.allowPixelResizing,
			),
		},

		nodes() {
			const {
				allowMediaGroup = true,
				allowMediaSingle = false,
				allowPixelResizing = false,
				allowCaptions,
				featureFlags: mediaFeatureFlags,
			} = options || {};

			const allowMediaInline = getMediaFeatureFlag('mediaInline', mediaFeatureFlags);

			const mediaSingleOption = {
				withCaption: allowCaptions,
				withExtendedWidthTypes: allowPixelResizing,
			};

			return [
				{ name: 'mediaGroup', node: mediaGroupSpecWithFixedToDOM() },
				{ name: 'mediaSingle', node: mediaSingleSpecWithFixedToDOM(mediaSingleOption) },
				{ name: 'media', node: mediaSpecWithFixedToDOM() },
				{ name: 'mediaInline', node: mediaInlineSpecWithFixedToDOM() },
			].filter((node) => {
				if (node.name === 'mediaGroup') {
					return allowMediaGroup;
				}

				if (node.name === 'mediaSingle') {
					return allowMediaSingle;
				}

				if (node.name === 'mediaInline') {
					return allowMediaInline;
				}

				return true;
			});
		},

		pmPlugins() {
			const pmPlugins: Array<PMPlugin> = [
				{
					name: 'media',
					plugin: ({
						schema,
						dispatch,
						getIntl,
						eventDispatcher,
						providerFactory,
						errorReporter,
						portalProviderAPI,
						dispatchAnalyticsEvent,
						nodeViewPortalProviderAPI,
					}: PMPluginFactoryParams) => {
						return createPlugin(
							schema,
							{
								providerFactory,
								nodeViews: {
									mediaGroup: lazyMediaGroupView(
										portalProviderAPI,
										eventDispatcher,
										providerFactory,
										options,
										api,
										getIntl(),
									),
									mediaSingle: ReactMediaSingleNode(
										portalProviderAPI,
										eventDispatcher,
										providerFactory,
										api,
										dispatchAnalyticsEvent,
										options,
										getIntl(),
									),
									media: ReactMediaNode(
										portalProviderAPI,
										eventDispatcher,
										providerFactory,
										options,
										api,
										getIntl(),
									),
									mediaInline: lazyMediaInlineView(
										portalProviderAPI,
										eventDispatcher,
										providerFactory,
										api,
										undefined,
										options?.fallbackMediaNameFetcher,
										getIntl(),
									),
								},
								errorReporter,
								uploadErrorHandler: options && options.uploadErrorHandler,
								waitForMediaUpload: options && options.waitForMediaUpload,
								customDropzoneContainer: options && options.customDropzoneContainer,
								customMediaPicker: options && options.customMediaPicker,
								allowResizing: !!(options && options.allowResizing),
								fallbackMediaNameFetcher: options && options.fallbackMediaNameFetcher,
							},
							getIntl,
							api,
							nodeViewPortalProviderAPI,
							dispatch,
							options,
						);
					},
				},
				{
					name: 'mediaKeymap',
					plugin: ({ getIntl }) =>
						keymapPlugin(
							options,
							api?.analytics?.actions,
							api?.selection?.actions,
							api?.width,
							getIntl,
						),
				},
			];

			if (options && options.allowMediaSingle) {
				pmPlugins.push({
					name: 'mediaSingleKeymap',
					plugin: ({ schema }) => keymapMediaSinglePlugin(schema),
				});
			}

			if (options && options.allowAltTextOnImages) {
				pmPlugins.push({
					name: 'mediaAltText',
					plugin: createMediaAltTextPlugin,
				});
				pmPlugins.push({
					name: 'mediaAltTextKeymap',
					plugin: ({ schema }) => keymapMediaAltTextPlugin(schema, api?.analytics?.actions),
				});
			}

			if (options && options.allowLinking) {
				pmPlugins.push({
					name: 'mediaLinking',
					plugin: ({ dispatch }: PMPluginFactoryParams) => linkingPlugin(dispatch),
				});
				pmPlugins.push({
					name: 'mediaLinkingKeymap',
					plugin: ({ schema }) => keymapLinkingPlugin(schema),
				});
			}

			if (
				options &&
				options.allowAdvancedToolBarOptions &&
				options.allowResizing &&
				areToolbarFlagsEnabled(Boolean(api?.toolbar)) &&
				options.allowPixelResizing
			) {
				pmPlugins.push({
					name: 'mediaPixelResizing',
					plugin: createMediaPixelResizingPlugin,
				});
			}

			pmPlugins.push({
				name: 'mediaAIGeneratingDecoration',
				plugin: () => createAIGeneratingDecorationPlugin(),
			});

			pmPlugins.push({
				name: 'mediaSelectionHandler',
				plugin: () => {
					const mediaSelectionHandlerPlugin = new SafePlugin({
						key: new PluginKey('mediaSelectionHandlerPlugin'),
						props: {
							handleScrollToSelection: (view) => {
								const {
									state: { selection },
								} = view;
								if (!(selection instanceof NodeSelection) || selection.node.type.name !== 'media') {
									return false;
								}

								const { node, offset } = view.domAtPos(selection.from);
								if (
									// Is the media element mounted already?
									offset === node.childNodes.length
								) {
									// Media is not ready, so stop the scroll request
									return true;
								}

								// Media is ready, keep the scrolling request
								return false;
							},
						},
					});

					return mediaSelectionHandlerPlugin;
				},
			});

			return pmPlugins;
		},

		contentComponent({ editorView, appearance }) {
			if (!editorView) {
				return null;
			}

			return (
				<>
					<MediaViewerFunctionalComponent
						api={api}
						editorView={editorView}
						mediaViewerExtensions={options?.mediaViewerExtensions}
						fallbackMediaNameFetcher={options?.fallbackMediaNameFetcher}
					/>
					<MediaPickerFunctionalComponent
						editorDomElement={editorView.dom}
						appearance={appearance}
						api={api}
					/>
				</>
			);
		},

		secondaryToolbarComponent({ disabled }) {
			return <ToolbarMedia isDisabled={disabled} isReducedSpacing={true} api={api} />;
		},

		pluginsOptions: {
			quickInsert: ({ formatMessage }) =>
				!api?.mediaInsert
					? [
							{
								id: 'media',
								title: formatMessage(messages.mediaFiles),
								description: formatMessage(messages.mediaFilesDescription),
								priority: 400,
								keywords: ['attachment', 'gif', 'media', 'picture', 'image', 'video', 'file'],
								icon: () => <IconImages />,
								isDisabledOffline: true,
								action(insert, state) {
									const pluginState = stateKey.getState(state);
									pluginState?.showMediaPicker();
									const tr = insert('');
									api?.analytics?.actions.attachAnalyticsEvent({
										action: ACTION.OPENED,
										actionSubject: ACTION_SUBJECT.PICKER,
										actionSubjectId: ACTION_SUBJECT_ID.PICKER_CLOUD,
										attributes: { inputMethod: INPUT_METHOD.QUICK_INSERT },
										eventType: EVENT_TYPE.UI,
									})(tr);

									return tr;
								},
							},
						]
					: [],

			floatingToolbar: (state, intl, providerFactory) =>
				floatingToolbar(
					state,
					intl,
					{
						providerFactory,
						allowMediaInline: options && getMediaFeatureFlag('mediaInline', options.featureFlags),
						allowResizing: options && options.allowResizing,
						allowResizingInTables: options && options.allowResizingInTables,
						allowCommentsOnMedia: options && options.allowCommentsOnMedia,
						allowLinking: options && options.allowLinking,
						allowAdvancedToolBarOptions: options && options.allowAdvancedToolBarOptions,
						allowAltTextOnImages: options && options.allowAltTextOnImages,
						allowImageEditing: !!api?.mediaEditing && options && options.allowImageEditing,
						allowImagePreview: options && options.allowImagePreview,
						allowVideoPreview: options && options.allowVideoPreview,
						altTextValidator: options && options.altTextValidator,
						fullWidthEnabled: options && options.fullWidthEnabled,
						allowMediaInlineImages: options && options.allowMediaInlineImages,
						isViewOnly: api?.editorViewMode?.sharedState.currentState()?.mode === 'view',
						allowPixelResizing: options?.allowPixelResizing,
						onCommentButtonMount: options?.onCommentButtonMount,
						createCommentExperience: options?.createCommentExperience,
					},
					api,
				),
		},
	};
};
